import React, { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Collapse } from "@mui/material";
import ExplanationTooltip from "../../../../components/ExplanationTooltip/ExplanationTooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { TextInput } from "../../../../components/TextInput";
import CDTAndCustomProcedureSelect, {
  CDTAndCustomProcedureValue,
} from "../../../../components/CDTAndCustomProcedureSelect";
import {
  TrashIcon,
  ArrowsMoveIcon,
  PlusIcon,
} from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import {
  AppointmentCard,
  AppointmentHeader,
  AppointmentTitleContainer,
  AppointmentTitle,
  AppointmentBadge,
  AppointmentActions,
  CollapseButton,
  AppointmentContent,
  FieldRow,
  FieldHalf,
  ProceduresSection,
  ProcedureCard,
  ProcedureHeader,
  ProcedureTitle,
  ProcedureActions,
  ActionButton,
  DragHandle,
  ProcedureContent,
  ProcedureLeftColumn,
  ProcedureRightColumn,
  ProcedureDescriptionWrapper,
  ProcedureFieldsRow,
  AddProcedureButton,
} from "./EditableAppointment.styled";

// Editable procedure data
export interface EditableProcedureData {
  id?: number;
  text: string; // Step Name
  description: string; // Step Description
  sortOrder: number;
  cdtCodes: string[]; // Extra CDT codes beyond the custom bundle
  customProcedureId?: number; // Selected custom procedure (UI state)
  resolvedCdtCodes: string[]; // Full merged CDT code list for API saving
  explanation: string;
  durationMinutes: number;
  fee: string; // String to allow decimal input like "123.45"
  isFeeEdited: boolean;
}

// Editable appointment data
export interface EditableAppointmentData {
  id?: number;
  title: string;
  timeframeValue: string;
  timeframeUnit: string;
  conditionalPath: string;
  description: string;
  sortOrder: number;
  procedures: EditableProcedureData[];
}

// Validation errors structure for an appointment
export interface AppointmentValidationErrors {
  title?: string;
  description?: string;
  timeframeValue?: string;
  procedures?: {
    [procedureIndex: number]: {
      text?: string;
      description?: string;
      fee?: string;
      durationMinutes?: string;
    };
  };
}

interface EditableAppointmentProps {
  appointment: EditableAppointmentData;
  appointmentNumber: number;
  isLastAppointment: boolean;
  defaultExpanded?: boolean;
  forceExpanded?: boolean;
  forceExpandTrigger?: number;
  validationErrors?: AppointmentValidationErrors;
  onAppointmentDragStart?: (e: React.DragEvent) => void;
  onChange: (field: keyof EditableAppointmentData, value: unknown) => void;
  onProcedureChange: (
    procedureIndex: number,
    field: keyof EditableProcedureData,
    value: unknown,
  ) => void;
  onAddProcedure: () => void;
  onDeleteProcedure: (procedureIndex: number) => void;
  onDelete: () => void;
}

const EditableAppointment: React.FC<EditableAppointmentProps> = ({
  appointment,
  appointmentNumber,
  isLastAppointment,
  defaultExpanded = true,
  forceExpanded = false,
  forceExpandTrigger = 0,
  validationErrors,
  onAppointmentDragStart,
  onChange,
  onProcedureChange,
  onAddProcedure,
  onDeleteProcedure,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Force expand when there are validation errors
  // Using forceExpandTrigger ensures this runs on every validation attempt
  React.useEffect(() => {
    if (forceExpanded && forceExpandTrigger > 0) {
      setExpanded(true);
    }
  }, [forceExpanded, forceExpandTrigger]);

  // Drag state for procedures
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Drag refs for procedures
  const dragProcedure = useRef<number | null>(null);
  const dragOverProcedure = useRef<number | null>(null);

  // Handle CDT/custom procedure selection change
  const handleCdtChange = (
    procedureIndex: number,
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
    totalFee: number,
    totalDurationMinutes: number,
  ) => {
    onProcedureChange(procedureIndex, "cdtCodes", value.cdtCodes);
    onProcedureChange(
      procedureIndex,
      "customProcedureId",
      value.customProcedureId,
    );
    onProcedureChange(procedureIndex, "resolvedCdtCodes", allCdtCodes);
    onProcedureChange(
      procedureIndex,
      "explanation",
      allDescriptions.filter(Boolean).join("\n"),
    );
    onProcedureChange(procedureIndex, "fee", String(totalFee));
    onProcedureChange(procedureIndex, "durationMinutes", totalDurationMinutes);
  };

  // Get option label from conditionalPath
  const optionLabel = useMemo(() => {
    if (
      !appointment.conditionalPath ||
      appointment.conditionalPath.trim() === ""
    ) {
      return null;
    }
    const rawPath = appointment.conditionalPath.trim();
    // If conditionalPath already starts with "Option", return as-is
    // Otherwise, just return the raw value (translation will add "Option" prefix)
    return rawPath.toLowerCase().startsWith("option")
      ? rawPath.replace(/^option\s*/i, "") // Remove "Option " prefix for translation
      : rawPath;
  }, [appointment.conditionalPath]);

  // Handle procedure drag start
  const handleProcedureDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation();
    dragProcedure.current = index;
    setDraggingIndex(index);

    // Set the entire procedure card as the drag image
    const dragHandle = e.currentTarget as HTMLElement;
    const procedureCard = dragHandle.closest(
      "[data-procedure-card]",
    ) as HTMLElement;
    if (procedureCard) {
      const rect = procedureCard.getBoundingClientRect();
      e.dataTransfer.setDragImage(procedureCard, rect.width / 2, 20);
    }
  };

  // Handle procedure drag over - use this for visual feedback
  const handleProcedureDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingIndex !== null && draggingIndex !== index) {
      dragOverProcedure.current = index;
      setDragOverIndex(index);
    }
  };

  // Handle procedure drag leave
  const handleProcedureDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    // Only clear if leaving to outside, not to child elements
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  // Handle procedure drop
  const handleProcedureDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (dragProcedure.current !== null && dragProcedure.current !== index) {
      const newProcedures = [...appointment.procedures];
      const draggedItem = newProcedures[dragProcedure.current];
      newProcedures.splice(dragProcedure.current, 1);
      newProcedures.splice(index, 0, draggedItem);

      // Renumber procedures
      const renumberedProcedures = newProcedures.map((proc, idx) => ({
        ...proc,
        sortOrder: idx + 1,
      }));

      onChange("procedures", renumberedProcedures);
    }

    // Reset all drag states
    dragProcedure.current = null;
    dragOverProcedure.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  // Handle procedure drag end (cleanup)
  const handleProcedureDragEnd = () => {
    // Reset all drag states
    dragProcedure.current = null;
    dragOverProcedure.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  return (
    <AppointmentCard>
      <AppointmentHeader>
        <AppointmentTitleContainer>
          <AppointmentTitle>
            {t("treatmentPlan.appointment", { number: appointmentNumber })}
          </AppointmentTitle>
          {optionLabel && (
            <AppointmentBadge>
              {t("treatmentPlan.optionLabel", { label: optionLabel })}
            </AppointmentBadge>
          )}
        </AppointmentTitleContainer>
        <AppointmentActions>
          <ActionButton onClick={onDelete}>
            <TrashIcon width="18px" height="18px" fill={colors.status.error} />
          </ActionButton>
          <DragHandle draggable onDragStart={onAppointmentDragStart}>
            <ArrowsMoveIcon
              width="18px"
              height="18px"
              fill={colors.primary.main}
            />
          </DragHandle>
          <CollapseButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </CollapseButton>
        </AppointmentActions>
      </AppointmentHeader>

      <Collapse in={expanded}>
        <AppointmentContent>
          <FieldRow>
            <FieldHalf style={{ flex: isLastAppointment ? 1 : undefined }}>
              <TextInput
                name={`appointment-${appointmentNumber}-description`}
                label={t("editTreatmentPlan.appointmentDescription")}
                placeholder={t(
                  "editTreatmentPlan.appointmentDescriptionPlaceholder",
                )}
                value={appointment.description || ""}
                onChange={(e) => onChange("description", e.target.value)}
                error={!!validationErrors?.description}
                helperText={validationErrors?.description}
                maxLength={100}
                required
              />
            </FieldHalf>
            {!isLastAppointment && (
              <FieldHalf>
                <TextInput
                  name={`appointment-${appointmentNumber}-timeframe`}
                  label={t("editTreatmentPlan.timeUntilNextAppointment")}
                  placeholder={t(
                    "editTreatmentPlan.timeUntilNextAppointmentPlaceholder",
                  )}
                  value={String(appointment.timeframeValue || "")}
                  onChange={(e) => onChange("timeframeValue", e.target.value)}
                  error={!!validationErrors?.timeframeValue}
                  helperText={validationErrors?.timeframeValue}
                  required
                />
              </FieldHalf>
            )}
          </FieldRow>

          <ProceduresSection>
            {appointment.procedures
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((procedure, index) => (
                <ProcedureCard
                  key={procedure.id || `new-${index}`}
                  data-procedure-card
                  isDragging={draggingIndex === index}
                  isDragOver={dragOverIndex === index}
                  onDragOver={(e) => handleProcedureDragOver(e, index)}
                  onDragLeave={handleProcedureDragLeave}
                  onDrop={(e) => handleProcedureDrop(e, index)}
                  onDragEnd={handleProcedureDragEnd}
                >
                  <ProcedureHeader>
                    <ProcedureTitle>
                      {t("editTreatmentPlan.step", { number: index + 1 })}
                    </ProcedureTitle>
                    <ProcedureActions>
                      <ActionButton onClick={() => onDeleteProcedure(index)}>
                        <TrashIcon
                          width="18px"
                          height="18px"
                          fill={colors.status.error}
                        />
                      </ActionButton>
                      <DragHandle
                        draggable
                        onDragStart={(e) => handleProcedureDragStart(e, index)}
                      >
                        <ArrowsMoveIcon
                          width="18px"
                          height="18px"
                          fill={colors.primary.main}
                        />
                      </DragHandle>
                    </ProcedureActions>
                  </ProcedureHeader>
                  <ProcedureContent>
                    {/* Left column: name → duration/fee → CDT */}
                    <ProcedureLeftColumn>
                      <TextInput
                        name={`appointment-${appointmentNumber}-step-${index + 1}-name`}
                        label={t("editTreatmentPlan.stepName")}
                        placeholder={t("editTreatmentPlan.stepNamePlaceholder")}
                        value={procedure.text || ""}
                        onChange={(e) =>
                          onProcedureChange(index, "text", e.target.value)
                        }
                        error={!!validationErrors?.procedures?.[index]?.text}
                        helperText={validationErrors?.procedures?.[index]?.text}
                        required
                      />
                      <ProcedureFieldsRow>
                        <FieldHalf>
                          <TextInput
                            name={`appointment-${appointmentNumber}-step-${index + 1}-duration`}
                            label={t("editTreatmentPlan.durationMin")}
                            type="text"
                            value={String(procedure.durationMinutes ?? "")}
                            onChange={(e) => {
                              const filtered = e.target.value.replace(
                                /[^0-9]/g,
                                "",
                              );
                              onProcedureChange(
                                index,
                                "durationMinutes",
                                filtered === "" ? 0 : parseInt(filtered, 10),
                              );
                            }}
                            onBlur={() => {
                              if (procedure.durationMinutes == null) {
                                onProcedureChange(index, "durationMinutes", 0);
                              }
                            }}
                            error={
                              !!validationErrors?.procedures?.[index]
                                ?.durationMinutes
                            }
                            helperText={
                              validationErrors?.procedures?.[index]
                                ?.durationMinutes
                            }
                            required
                          />
                        </FieldHalf>
                        <FieldHalf>
                          <TextInput
                            name={`appointment-${appointmentNumber}-step-${index + 1}-fee`}
                            label={t("editTreatmentPlan.fee")}
                            type="text"
                            value={procedure.fee}
                            onChange={(e) => {
                              let value = e.target.value
                                .replace(/[^0-9.]/g, "")
                                .replace(/(\..*)\./g, "$1");
                              if (
                                value.length > 1 &&
                                value.startsWith("0") &&
                                value[1] !== "."
                              ) {
                                value = value.replace(/^0+/, "") || "0";
                              }
                              onProcedureChange(index, "fee", value);
                            }}
                            onBlur={() => {
                              if (
                                !procedure.fee ||
                                procedure.fee.trim() === ""
                              ) {
                                onProcedureChange(index, "fee", "0");
                              }
                            }}
                            error={!!validationErrors?.procedures?.[index]?.fee}
                            helperText={
                              validationErrors?.procedures?.[index]?.fee
                            }
                            required
                          />
                        </FieldHalf>
                      </ProcedureFieldsRow>
                      <CDTAndCustomProcedureSelect
                        name={`appointment-${appointmentNumber}-step-${index + 1}-cdt`}
                        label={t("editTreatmentPlan.cdtCode")}
                        placeholder={t("editTreatmentPlan.cdtCodesPlaceholder")}
                        value={{
                          cdtCodes: procedure.cdtCodes,
                          customProcedureId: procedure.customProcedureId,
                        }}
                        onChange={(
                          value,
                          allCdtCodes,
                          allDescriptions,
                          totalFee,
                          totalDurationMinutes,
                        ) =>
                          handleCdtChange(
                            index,
                            value,
                            allCdtCodes,
                            allDescriptions,
                            totalFee,
                            totalDurationMinutes,
                          )
                        }
                        fullWidth
                      />
                    </ProcedureLeftColumn>
                    {/* Right column: step description spanning the full left-column height */}
                    <ProcedureRightColumn>
                      <ProcedureDescriptionWrapper>
                        <TextInput
                          name={`appointment-${appointmentNumber}-step-${index + 1}-description`}
                          label={t("editTreatmentPlan.stepDescription")}
                          placeholder={t(
                            "editTreatmentPlan.stepDescriptionPlaceholder",
                          )}
                          value={procedure.description || ""}
                          onChange={(e) =>
                            onProcedureChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          error={
                            !!validationErrors?.procedures?.[index]?.description
                          }
                          helperText={
                            validationErrors?.procedures?.[index]?.description
                          }
                          required
                          multiline
                        />
                      </ProcedureDescriptionWrapper>
                    </ProcedureRightColumn>
                  </ProcedureContent>
                  {/* Explanation sits below the two-column area */}
                  <Box sx={{ marginTop: "6px" }}>
                    <ExplanationTooltip
                      explanation={procedure.explanation || ""}
                    />
                  </Box>
                </ProcedureCard>
              ))}
            <AddProcedureButton>
              <Button
                size="small"
                variant="outlined"
                onClick={onAddProcedure}
                startIcon={
                  <PlusIcon
                    width="16px"
                    height="16px"
                    fill={colors.primary.main}
                  />
                }
                sx={{
                  color: colors.primary.main,
                  borderColor: colors.primary.main,
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: colors.primary.dark,
                    backgroundColor: "rgba(58, 138, 185, 0.04)",
                  },
                }}
              >
                {t("editTreatmentPlan.addStep")}
              </Button>
            </AddProcedureButton>
          </ProceduresSection>
        </AppointmentContent>
      </Collapse>
    </AppointmentCard>
  );
};

export default EditableAppointment;
